import { useEffect, useMemo, useState } from "react";
import VideoCard from "@/components/feature/VideoCard";
import VideoPlayerModal from "@/components/feature/VideoPlayerModal";
import { fetchCommunityVideos, fetchModules, type CommunityModule, type CommunityVideo } from "@/services/communityVideos";

/** Videos por módulo que sube el administrador. Si no hay ninguno publicado, no se muestra nada. */
export default function CourseVideosSection() {
  const [modules, setModules] = useState<CommunityModule[]>([]);
  const [videos, setVideos] = useState<CommunityVideo[]>([]);
  const [moduleId, setModuleId] = useState<string | null>(null);
  const [playing, setPlaying] = useState<CommunityVideo | null>(null);

  useEffect(() => {
    Promise.all([fetchModules(), fetchCommunityVideos()])
      .then(([m, v]) => {
        setModules(m.filter((x) => x.active));
        setVideos(v.filter((x) => x.published));
      })
      .catch(() => {});
  }, []);

  const withVideos = useMemo(() => modules.filter((m) => videos.some((v) => v.moduleId === m.id)), [modules, videos]);
  const active = moduleId && withVideos.some((m) => m.id === moduleId) ? moduleId : withVideos[0]?.id ?? null;
  const list = useMemo(
    () => videos.filter((v) => v.moduleId === active).sort((a, b) => Number(b.featured) - Number(a.featured) || a.sortOrder - b.sortOrder),
    [videos, active]
  );
  if (!withVideos.length || !active) return null;
  const portraitList = list.filter((v) => v.layout === "portrait");
  const landscapeList = list.filter((v) => v.layout !== "portrait");

  return (
    <div className="mt-5 bg-background-100 border border-background-200 rounded-2xl p-4">
      <div className="flex items-center gap-2">
        <i className="ri-movie-2-line text-primary-600 text-lg"></i>
        <h2 className="font-heading font-bold text-foreground-950 text-base">Videos de los cursos</h2>
      </div>
      <div className="mt-3 flex gap-2 overflow-x-auto vv-scrollbar-hidden pb-1" role="tablist" aria-label="Módulos">
        {withVideos.map((m) => (
          <button
            key={m.id}
            role="tab"
            aria-selected={active === m.id}
            onClick={() => setModuleId(m.id)}
            className={`inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-semibold border cursor-pointer ${active === m.id ? "bg-primary-500 text-white border-primary-500" : "bg-background-50 text-foreground-700 border-background-200"}`}
          >
            <i className={m.icon}></i> {m.title}
          </button>
        ))}
      </div>
      {landscapeList.length ? (
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {landscapeList.map((v) => (
            <VideoCard key={v.id} video={v} onClick={() => setPlaying(v)} />
          ))}
        </div>
      ) : null}
      {portraitList.length ? (
        <div className="mt-3 grid gap-3 grid-cols-2 sm:grid-cols-3">
          {portraitList.map((v) => (
            <VideoCard key={v.id} video={v} onClick={() => setPlaying(v)} />
          ))}
        </div>
      ) : null}
      {playing ? <VideoPlayerModal url={playing.videoUrl} title={playing.title} onClose={() => setPlaying(null)} /> : null}
    </div>
  );
}
